import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { PropertyName } from "@frui.ts/helpers";
import type { ViewComponent } from "@frui.ts/views/dist";
import { registerView } from "@frui.ts/views/dist";
import { observer } from "mobx-react-lite";
import React from "react";
import { formatDate, formatDateTime } from "utils/localizations";
import ExemptionChangesViewModel from "viewModels/callCenter/search/exemptions/detailExemption/exemptionChangesViewModel";
import ExemptionPropertyChangeDisplayType from "entities/exemptionPropertyChangeDisplayType";
import ExemptionChangeOrigin from "entities/exemptionChangeOrigin";
import type ExemptionChangeResponse from "entities/exemptionChangeResponse";
import ChargeChangeInitiator from "entities/chargeChangeInitiator";
import { emptyValue } from "utils/emptyValue";
import type VehicleRegisterVehicleChange from "entities/vehicleRegisterVehicleChange";

// eslint-disable-next-line sonarjs/cognitive-complexity
const ExemptionChangesView: ViewComponent<ExemptionChangesViewModel> = observer(({ vm }) => {
  if (!vm.items) {
    return null;
  }

  function changeValueFormatter(item: ExemptionChangeResponse, type: "new" | "original") {
    const value = type === "new" ? item.newValue : item.originalValue;
    if (!value) {
      return emptyValue;
    }
    const isEnumValue = Object.values(ExemptionPropertyChangeDisplayType).includes(value as ExemptionPropertyChangeDisplayType);
    const translateValue = isEnumValue
      ? vm.localizationService.translateEnum("exemptionPropertyChangeDisplayType", value as ExemptionPropertyChangeDisplayType)
      : null;

    if (translateValue) {
      return translateValue;
    }

    switch (item.changeType) {
      case ExemptionPropertyChangeDisplayType.ExemptionReasonIdChange:
        return vm.translateExemptionReasonId(value);
      case ExemptionPropertyChangeDisplayType.VehicleRegisterVehicleIdChange:
      case ExemptionPropertyChangeDisplayType.VehicleRegisterVehiclePropertiesChange:
        return emptyValue;

      case ExemptionPropertyChangeDisplayType.SingleCountryChange:
      case ExemptionPropertyChangeDisplayType.BulkCountryChange:
        return vm.translateCountry(value);

      case ExemptionPropertyChangeDisplayType.ValidSinceChange:
      case ExemptionPropertyChangeDisplayType.ValidUntilChange: {
        return <span className="text-nowrap">{formatDateTime(new Date(value))}</span>;
      }

      default:
        return value;
    }
  }

  function changeVehicleValueFormatter(key: string, value: any) {
    let formatedValue = emptyValue;

    switch (key) {
      case "updatedAt":
        formatedValue = value ? formatDateTime(new Date(value)) : emptyValue;
        break;
      case "licensePlateValidSinceDate":
      case "licensePlateValidUntilDate":
      case "vehicleStatusValidSinceDate":
      case "vehicleStatusValidUntilDate":
        formatedValue = value ? formatDate(new Date(value)) : emptyValue;
        break;
      case "fuelTypes":
        formatedValue = value ? vm.translateVehicleRegisterVehicleFuelType(value) : emptyValue;
        break;
      case "maximumAllowedWeight":
        formatedValue = value ? `${value} kg` : emptyValue;
        break;
      case "classifiedFuelType":
        formatedValue = value ? vm.translateVehicleRegisterVehicleClassifiedFuelType(value) : emptyValue;
        break;
      case "vehicleStatus":
        formatedValue = value ? vm.translateVehicleStatus(value) : emptyValue;
        break;
      default:
        formatedValue = value ?? emptyValue;
        break;
    }

    return formatedValue.toString();
  }

  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <table className="table table-full table-tbody-striped tbody-larger">
          <thead>
            <tr>
              <th>{vm.translateAttribute("changedAt")}</th>
              <th>{vm.translateChangeAttribute("changeType")}</th>
              <th>{vm.translateChangeAttribute("originalValue")}</th>
              <th style={{ width: 70 }}></th>
              <th>{vm.translateChangeAttribute("newValue")}</th>
              <th>{vm.translateAttribute("changedBy")}</th>
              <th>{vm.translateAttribute("changeOrigin")}</th>
              <th></th>
            </tr>
          </thead>
          <>
            {vm.transformedItems.map((item, indexItem) => {
              const detailRSV: JSX.Element[] = [];
              const showDetail = vm.canShowDetail(item);

              if (item.newVehicleRegisterVehicle || item.originalVehicleRegisterVehicle) {
                let obj = item.originalVehicleRegisterVehicle ? item.originalVehicleRegisterVehicle : undefined;
                if (!obj) {
                  obj = item.newVehicleRegisterVehicle;
                }
                obj &&
                  Object.keys(obj).forEach((key: string, indexDetail) => {
                    const originalValue = item.originalVehicleRegisterVehicle
                      ? item.originalVehicleRegisterVehicle[key as keyof VehicleRegisterVehicleChange]
                      : undefined;
                    const updatedValue = item.newVehicleRegisterVehicle
                      ? item.newVehicleRegisterVehicle[key as keyof VehicleRegisterVehicleChange]
                      : undefined;
                    if (key === "updatedAt") {
                      return;
                    }
                    const originalString = originalValue ? changeVehicleValueFormatter(key, originalValue) : emptyValue;
                    const updatedString = updatedValue ? changeVehicleValueFormatter(key, updatedValue) : emptyValue;
                    const isDiff =
                      originalString !== updatedString &&
                      item.changeType === ExemptionPropertyChangeDisplayType.VehicleRegisterVehiclePropertiesChange;

                    detailRSV.push(
                      <tr
                        key={`${item.changedAt}-${key}`}
                        className={`tr-narrow ${item._showDetail ? "show-detail" : "hidden-detail"} ${
                          (indexItem + 1 + indexDetail + 1) % 2 === 0 ? "even" : "odd"
                        } ${isDiff ? "diff" : ""}`}>
                        <td></td>
                        <td>{vm.translateAttribute(key as PropertyName<ExemptionChangeResponse>)}</td>
                        <td>{originalValue ? changeVehicleValueFormatter(key, originalValue) : emptyValue}</td>
                        <td>→</td>
                        <td>{updatedValue ? changeVehicleValueFormatter(key, updatedValue) : emptyValue}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    );
                  });
              }
              return (
                <>
                  <tbody key={`${item.changedAt}-${item.changeOrigin}`}>
                    <tr key={`${item.changedAt}-${item.changeType}`} className={`${(indexItem + 1) % 2 === 0 ? "even" : "odd"}`}>
                      <td>{formatDateTime(item.changedAt)}</td>
                      <td>
                        <strong className="text-primary">{vm.translateChangedExemptionProperty(item, item.changeType)}</strong>
                      </td>
                      <td>{changeValueFormatter(item, "original")}</td>
                      <td>→</td>
                      <td>{changeValueFormatter(item, "new")}</td>
                      <td>
                        {item.changeOrigin === ExemptionChangeOrigin.VehicleRegister
                          ? vm.translateChargeChangeInitiator(ChargeChangeInitiator.System)
                          : item.changedBy}
                      </td>
                      <td>{vm.translateExemptionChangeOrigin(item.changeOrigin)}</td>
                      <td>
                        {showDetail && (
                          <Button
                            onClick={() => vm.handleShowDetail(item)}
                            variant="light"
                            size="sm"
                            iconRight={item._showDetail ? IconType.ArrowTop : IconType.ArrowBottom}
                          />
                        )}
                      </td>
                    </tr>
                    <>
                      {showDetail &&
                        detailRSV?.map((item: any) => {
                          return item;
                        })}
                    </>
                  </tbody>
                </>
              );
            })}
          </>
        </table>
        {vm.items?.length === 0 && (
          <div className="bg-white mt-n2 mb-4 px-4 py-3 border-bottom text-muted full-width">
            {vm.translateGeneral(vm.busyWatcher.isBusy ? "loading" : "empty")}
          </div>
        )}
      </LoaderOverlay>
    </>
  );
});

registerView(ExemptionChangesView, ExemptionChangesViewModel);
